import { Link } from "@tanstack/react-router";
import { Check, Loader2, Star } from "lucide-react";
import type { JSX, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { Template } from "@/common/types/templates";
import { Button } from "@/components/ui/button";
import {
  GROUP_LABEL,
  LIST_CARD,
  TOUCH_BUTTON,
  TOUCH_ICON_BUTTON,
} from "@/components/ui/redesign/page";
import { useTemplatePreferences } from "@/hooks/templates/useTemplatePreferences";
import { toast } from "@/hooks/use-toast";
import {
  splitFavorites,
  type TemplateChoice,
  templateChoices,
} from "@/lib/template-preferences";
import { cn } from "@/lib/utils";

type Preferences = ReturnType<typeof useTemplatePreferences>;

export const FavoriteStar = ({
  choice,
  favorite,
  onToggle,
}: {
  choice: TemplateChoice;
  favorite: boolean;
  onToggle: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const label = t(
    favorite ? "templates.favorites.unstar" : "templates.favorites.star",
    { name: choice.name },
  );
  return (
    <Button
      aria-label={label}
      aria-pressed={favorite}
      className={cn(
        TOUCH_ICON_BUTTON,
        "shrink-0 rounded-full",
        favorite ? "text-v3-warn" : "text-muted-foreground",
      )}
      data-testid="template-star"
      size="icon"
      title={label}
      type="button"
      variant="ghost"
      onClick={onToggle}
    >
      <Star aria-hidden className={cn(favorite && "fill-current")} />
    </Button>
  );
};

const DefaultAction = ({
  choice,
  prefs,
}: {
  choice: TemplateChoice;
  prefs: Preferences;
}): JSX.Element => {
  const { t } = useTranslation();
  if (prefs.defaultTemplate === choice.value)
    return (
      <span
        className="inline-flex h-7 shrink-0 items-center gap-1 rounded-full bg-v3-brand-tint px-2.5 text-xs font-semibold text-v3-brand"
        data-testid="template-default"
      >
        <Check aria-hidden className="size-3.5" />
        {t("templates.favorites.isDefault")}
      </span>
    );
  const saving = prefs.saving === choice.value;
  return (
    <Button
      aria-label={t("templates.favorites.setDefaultFor", {
        name: choice.name,
      })}
      className={cn(TOUCH_BUTTON, "shrink-0 px-2.5 text-v3-brand")}
      data-testid="template-set-default"
      disabled={!!prefs.saving || prefs.loading}
      size="sm"
      type="button"
      variant="ghost"
      onClick={() => {
        void prefs.setDefault(choice.value).then((saved) => {
          toast(
            saved
              ? {
                  variant: "success",
                  title: t("templates.favorites.defaultSaved", {
                    name: choice.name,
                  }),
                }
              : {
                  variant: "error",
                  title: t("templates.favorites.defaultFailed"),
                },
          );
        });
      }}
    >
      {saving && <Loader2 aria-hidden className="animate-spin" />}
      {t("templates.favorites.setDefault")}
    </Button>
  );
};

const Row = ({
  choice,
  prefs,
  actions,
  actionsClassName,
}: {
  choice: TemplateChoice;
  prefs: Preferences;
  actions?: ReactNode;
  // Width kept for the actions, so built-in rows (none) line up.
  actionsClassName?: string;
}): JSX.Element => {
  const { t } = useTranslation();
  const sections = choice.template?.headers?.length ?? 0;
  const preview = (choice.template?.headers ?? []).slice(0, 4).join(" · ");
  const title = (
    <span className="min-w-0 flex-1">
      <span className="block truncate text-[15px] font-semibold leading-6">
        {choice.name}
      </span>
      <span className="flex min-w-0 items-center gap-2 text-[13px] text-muted-foreground">
        <span className="shrink-0">
          {choice.builtIn
            ? t("templates.favorites.builtIn")
            : t("templates.home.sections", { count: sections })}
        </span>
        {preview && (
          <span className="min-w-0 truncate max-sm:hidden">· {preview}</span>
        )}
      </span>
    </span>
  );
  return (
    <li
      className="flex min-h-16 items-center gap-1 py-2 pl-1.5 pr-2 transition-colors hover:bg-accent/40 sm:gap-2 sm:pl-3 sm:pr-3"
      data-template={choice.value}
      data-testid="template-choice"
    >
      <FavoriteStar
        choice={choice}
        favorite={prefs.favorites.includes(choice.value)}
        onToggle={() => {
          prefs.toggleFavorite(choice.value);
        }}
      />
      {choice.builtIn ? (
        <span className="flex min-w-0 flex-1 py-1">{title}</span>
      ) : (
        <Link
          className="flex min-w-0 flex-1 rounded-md py-1 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="template-row"
          params={{ templatePublicId: choice.value }}
          to="/template/$templatePublicId"
        >
          {title}
        </Link>
      )}
      <DefaultAction choice={choice} prefs={prefs} />
      {actionsClassName !== undefined && (
        <div className={cn("flex shrink-0 justify-end", actionsClassName)}>
          {actions}
        </div>
      )}
    </li>
  );
};

const Group = ({
  label,
  testId,
  children,
}: {
  label: ReactNode;
  testId: string;
  children: ReactNode;
}): JSX.Element => (
  <section data-testid={testId}>
    <h3 className={cn(GROUP_LABEL, "flex items-center gap-1.5 px-1")}>
      {label}
    </h3>
    <ul className={cn(LIST_CARD, "divide-y")}>{children}</ul>
  </section>
);

interface Props {
  templates: Template[];
  // Hide built-in and custom templates that don't match a search.
  matches?: (choice: TemplateChoice) => boolean;
  // Edit / delete controls for a custom template, and the width they take.
  renderActions?: (template: Template) => ReactNode;
  actionsClassName?: string;
  // Shown in place of the "My templates" group when there are none.
  emptyYours?: ReactNode;
}

// Templates grouped as Favorites (starred), My templates and Built-in. Each
// row has a star and "Set as default"; the default shows a badge instead.
export const TemplateGroups = ({
  templates,
  matches = () => true,
  renderActions,
  actionsClassName,
  emptyYours,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const prefs = useTemplatePreferences();
  const choices = templateChoices(templates).filter(matches);
  const { favorites, others } = splitFavorites(choices, prefs.favorites);
  const yours = others.filter((choice) => !choice.builtIn);
  const builtIn = others.filter((choice) => choice.builtIn);
  const row = (choice: TemplateChoice) => (
    <Row
      key={choice.value}
      actions={choice.template && renderActions?.(choice.template)}
      actionsClassName={renderActions ? (actionsClassName ?? "") : undefined}
      choice={choice}
      prefs={prefs}
    />
  );

  return (
    <div className="space-y-5" data-testid="template-groups">
      <p className="px-1 text-sm text-muted-foreground">
        {t("templates.favorites.hint")}
      </p>
      {favorites.length > 0 && (
        <Group
          label={
            <>
              <Star
                aria-hidden
                className="size-3.5 fill-v3-warn text-v3-warn"
              />
              {t("templates.favorites.group")}
            </>
          }
          testId="template-group-favorites"
        >
          {favorites.map(row)}
        </Group>
      )}
      {yours.length > 0 ? (
        <Group
          label={t("templates.favorites.yoursGroup")}
          testId="template-group-yours"
        >
          {yours.map(row)}
        </Group>
      ) : (
        templates.length === 0 && emptyYours
      )}
      {builtIn.length > 0 && (
        <Group
          label={t("templates.favorites.builtInGroup")}
          testId="template-group-built-in"
        >
          {builtIn.map(row)}
        </Group>
      )}
    </div>
  );
};
